/**
 * CourierDeliveryDetailPage — Futaros kezbesites reszletei
 * Epic 71: Futaros Kezbesites
 *
 * Reszletes nezet: statusz lepesek, cim, futar info, muveletek.
 */

import type { CourierDeliveryStatus } from '@/api/courier-delivery';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useCourierDelivery,
  useCreatePaymentLink,
  useHandoffToCourier,
  useMarkDelivered,
  useUpdateCourierStatus,
} from '@/hooks/useCourierDelivery';
import { useTranslation } from '@kgc/i18n';
import { useNavigate, useParams } from 'react-router-dom';

const STATUS_LABEL_KEYS: Record<string, string> = {
  REQUESTED: 'courier.statusLabels.requested',
  PAYMENT_PENDING: 'courier.statusLabels.paymentPending',
  PAID: 'courier.statusLabels.paid',
  DISPATCHED: 'courier.statusLabels.dispatched',
  DELIVERED: 'courier.statusLabels.delivered',
  CANCELLED: 'courier.statusLabels.cancelled',
};

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  PAYMENT_PENDING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  PAID: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  DISPATCHED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  DELIVERED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const STATUS_STEPS: CourierDeliveryStatus[] = [
  'REQUESTED',
  'PAYMENT_PENDING',
  'PAID',
  'DISPATCHED',
  'DELIVERED',
];

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU');
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('hu-HU');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function CourierDeliveryDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { delivery, isLoading, error, refetch } = useCourierDelivery(id);
  const handoffMutation = useHandoffToCourier();
  const deliveredMutation = useMarkDelivered();
  const cancelMutation = useUpdateCourierStatus();
  const paymentLinkMutation = useCreatePaymentLink();

  const handleHandoff = async () => {
    if (!id) return;
    const provider = prompt(t('courier.enterProvider'));
    if (!provider) return;
    const trackingNumber = prompt(t('courier.enterTracking')) ?? undefined;
    await handoffMutation.mutateAsync({ id, data: { courierProvider: provider, trackingNumber } });
    refetch();
  };

  const handleDelivered = async () => {
    if (!id) return;
    await deliveredMutation.mutateAsync(id);
    refetch();
  };

  const handleCancel = async () => {
    if (!id) return;
    await cancelMutation.mutateAsync({ id, status: 'CANCELLED' });
    refetch();
  };

  const handleCreatePaymentLink = async () => {
    if (!id) return;
    const amountStr = prompt(t('courier.enterAmount'));
    if (!amountStr) return;
    const amount = Number(amountStr);
    if (isNaN(amount) || amount <= 0) return;
    await paymentLinkMutation.mutateAsync({
      deliveryId: id,
      data: { amount, description: t('courier.paymentDescription') },
    });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !delivery) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500">{error ?? t('courier.notFound')}</p>
            <Button className="mt-4" onClick={() => navigate('/worksheet/courier')}>
              {t('common.backToList')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStepIndex =
    delivery.status === 'CANCELLED' ? -1 : STATUS_STEPS.indexOf(delivery.status);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('courier.titleSingle')}</h1>
          <p className="text-muted-foreground">#{delivery.id.slice(0, 8)}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/worksheet/courier')}>
          {t('common.back')}
        </Button>
      </div>

      {/* Status Steps */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t('courier.statusProgress')}</CardTitle>
        </CardHeader>
        <CardContent>
          {delivery.status === 'CANCELLED' ? (
            <div className="flex items-center justify-center py-4">
              <span
                className={`px-4 py-2 rounded-lg text-sm font-medium ${STATUS_COLORS['CANCELLED']}`}
              >
                {t(STATUS_LABEL_KEYS['CANCELLED'])}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              {STATUS_STEPS.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                return (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        } ${isCurrent ? 'ring-2 ring-green-300' : ''}`}
                      >
                        {index + 1}
                      </div>
                      <span
                        className={`text-xs mt-1 text-center ${
                          isCompleted ? 'text-green-600 font-medium' : 'text-muted-foreground'
                        }`}
                      >
                        {t(STATUS_LABEL_KEYS[step])}
                      </span>
                    </div>
                    {index < STATUS_STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 ${
                          index < currentStepIndex ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Delivery Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('courier.deliveryInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('courier.statusLabel')}</span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[delivery.status] ?? ''}`}
              >
                {STATUS_LABEL_KEYS[delivery.status]
                  ? t(STATUS_LABEL_KEYS[delivery.status])
                  : delivery.status}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('courier.worksheetIdLabel')}</span>
              <span className="font-mono text-sm">{delivery.worksheetId.slice(0, 12)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('courier.addressLabel')}</span>
              <span className="text-right max-w-[60%]">{delivery.address}</span>
            </div>
            {delivery.scheduledDate && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('courier.scheduledLabel')}</span>
                <span>{formatDate(delivery.scheduledDate)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('courier.createdAtLabel')}</span>
              <span>{formatDateTime(delivery.createdAt)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Courier Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('courier.courierInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {delivery.courierProvider && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('courier.providerLabel')}</span>
                <span>{delivery.courierProvider}</span>
              </div>
            )}
            {delivery.trackingNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('courier.trackingLabel')}</span>
                <span className="font-mono text-sm">{delivery.trackingNumber}</span>
              </div>
            )}
            {delivery.courierCost != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('courier.costLabel')}</span>
                <span className="font-bold">{formatCurrency(delivery.courierCost)}</span>
              </div>
            )}
            {delivery.handoffAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('courier.handoffAtLabel')}</span>
                <span>{formatDateTime(delivery.handoffAt)}</span>
              </div>
            )}
            {delivery.deliveredAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('courier.deliveredAtLabel')}</span>
                <span>{formatDateTime(delivery.deliveredAt)}</span>
              </div>
            )}
            {!delivery.courierProvider && !delivery.trackingNumber && !delivery.handoffAt && (
              <p className="text-muted-foreground text-sm">{t('courier.noCourierYet')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Link Section */}
      {delivery.status === 'PAYMENT_PENDING' && (
        <Card className="mb-6 border-orange-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-orange-600">{t('courier.paymentSection')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">{t('courier.paymentPendingInfo')}</p>
            <Button onClick={handleCreatePaymentLink} disabled={paymentLinkMutation.isPending}>
              {paymentLinkMutation.isPending
                ? t('common.processing')
                : t('courier.createPaymentLink')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {delivery.status !== 'DELIVERED' && delivery.status !== 'CANCELLED' && (
        <div className="flex gap-3">
          {delivery.status === 'PAID' && (
            <Button onClick={handleHandoff} disabled={handoffMutation.isPending}>
              {handoffMutation.isPending ? t('common.processing') : t('courier.handoff')}
            </Button>
          )}
          {delivery.status === 'DISPATCHED' && (
            <Button onClick={handleDelivered} disabled={deliveredMutation.isPending}>
              {deliveredMutation.isPending ? t('common.processing') : t('courier.markDelivered')}
            </Button>
          )}
          <Button variant="destructive" onClick={handleCancel} disabled={cancelMutation.isPending}>
            {cancelMutation.isPending ? t('common.processing') : t('courier.cancelDelivery')}
          </Button>
        </div>
      )}
    </div>
  );
}
